class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.closeTimer = 0;
    this.openTimer = 0;
    this.closeAnimationHandler = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.clearCloseAnimation();
    window.clearTimeout(this.openTimer);
    this.classList.remove('is-closing');
    this.removeAttribute('aria-hidden');
    this.removeAttribute('inert');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    const surface = this.querySelector('.drawer__inner');
    this.classList.add('animate', 'active');
    this.classList.remove('is-opening');
    void this.offsetWidth;
    this.classList.add('is-opening');

    const finishOpen = () => {
      if (!this.classList.contains('active') || this.classList.contains('is-closing')) return;
      this.classList.remove('is-opening');
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = surface || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    this.openTimer = window.setTimeout(
      finishOpen,
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 440
    );

    if (window.NaturalOverlayLock) {
      window.NaturalOverlayLock.lock(this, this.querySelector('.drawer__inner'));
    } else {
      document.documentElement.classList.add('natural-scroll-locked');
      document.body.classList.add('natural-scroll-locked');
    }
    document.body.classList.add('overflow-hidden', 'classic');
  }

  close() {
    if (!this.classList.contains('active') || this.classList.contains('is-closing')) return;
    window.clearTimeout(this.openTimer);
    this.classList.remove('is-opening');
    this.classList.add('is-closing');
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('inert', '');
    removeTrapFocus(this.activeElement);

    const finishClose = () => {
      this.clearCloseAnimation();
      this.classList.remove('active', 'is-closing');
      this.removeAttribute('aria-hidden');
      this.removeAttribute('inert');
      const releasePage = () => {
        if (this.classList.contains('active')) return;
        if (window.NaturalOverlayLock) {
          window.NaturalOverlayLock.unlock(this);
        } else {
          document.documentElement.classList.remove('natural-scroll-locked');
          document.body.classList.remove('natural-scroll-locked');
        }
        document.body.classList.remove('overflow-hidden', 'classic');
      };

      // Let the hidden Cart frame reach the compositor before releasing the
      // fixed background. This avoids the one-frame mobile repaint flash.
      window.requestAnimationFrame(() => window.requestAnimationFrame(releasePage));
    };

    const surface = this.querySelector('.drawer__inner');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !surface) {
      finishClose();
      return;
    }

    this.closeAnimationHandler = (event) => {
      if (event.target === surface) finishClose();
    };
    surface.addEventListener('animationend', this.closeAnimationHandler);
    this.closeTimer = window.setTimeout(finishClose, 480);
  }

  clearCloseAnimation() {
    window.clearTimeout(this.closeTimer);
    this.closeTimer = 0;
    const surface = this.querySelector('.drawer__inner');
    if (surface && this.closeAnimationHandler) {
      surface.removeEventListener('animationend', this.closeAnimationHandler);
    }
    this.closeAnimationHandler = null;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
